/**
 * Plugin Registry - manages all loaded plugins
 */

import { TraceKitPlugin, NavItem, RouteDefinition } from '../types/plugins';

export class PluginRegistry {
  private plugins: Map<string, TraceKitPlugin> = new Map();

  register(plugin: TraceKitPlugin) {
    if (this.plugins.has(plugin.id)) {
      console.warn(`Plugin ${plugin.id} is already registered, skipping`);
      return;
    }
    
    console.log(`Registering plugin: ${plugin.name} (${plugin.version})`);
    this.plugins.set(plugin.id, plugin);
  }

  unregister(pluginId: string) {
    const plugin = this.plugins.get(pluginId);
    if (plugin && plugin.onDestroy) {
      plugin.onDestroy();
    }
    this.plugins.delete(pluginId);
  }

  getPlugin(pluginId: string): TraceKitPlugin | undefined {
    return this.plugins.get(pluginId);
  }

  getAllPlugins(): TraceKitPlugin[] {
    return Array.from(this.plugins.values());
  }

  // Aggregate routes from all plugins
  getAllRoutes(): RouteDefinition[] {
    const routes: RouteDefinition[] = [];
    for (const plugin of this.plugins.values()) {
      if (plugin.routes) {
        routes.push(...plugin.routes);
      }
    }
    return routes;
  }

  // Aggregate sidebar items from all plugins
  getAllSidebarItems(): NavItem[] {
    const items: NavItem[] = [];
    for (const plugin of this.plugins.values()) {
      if (plugin.sidebarItems) {
        items.push(...plugin.sidebarItems);
      }
    }
    return items;
  }

  // Get entity handler for specific type
  getEntityHandler(entityType: string): any | null {
    for (const plugin of this.plugins.values()) {
      if (plugin.entityTypeHandlers?.has(entityType)) {
        return plugin.entityTypeHandlers.get(entityType);
      }
    }
    return null;
  }

  // Get visualization provider
  getVisualizationProvider(vizId: string): any | null {
    for (const plugin of this.plugins.values()) {
      if (plugin.visualizationProviders?.has(vizId)) {
        return plugin.visualizationProviders.get(vizId);
      }
    }
    return null;
  }
}

// Global registry instance
export const pluginRegistry = new PluginRegistry();


